import type { BoardMeta } from '@os/doc';
import type { Camera } from '@os/engine';

export const PATTERN_BASE = 24;
const MIN_SCREEN_PX = 8;

/** World spacing of the pattern: the base doubled until it is at least 8 screen px apart (never denser on screen). */
export function patternStep(z: number, base = PATTERN_BASE): number {
  let step = base;
  while (step * z < MIN_SCREEN_PX && step < 1e9) step *= 2;
  return step;
}

const mod = (a: number, n: number) => ((a % n) + n) % n;

/**
 * Inline style of the background layer for the camera: color, pattern, its size and its offset from the world origin.
 * `base`: the board's grid size, so the dots show where objects snap (P16).
 */
export function backgroundCss(bg: BoardMeta['background'], cam: Camera, base = PATTERN_BASE) {
  const s = patternStep(cam.z, base) * cam.z;
  const size = `${+s.toFixed(3)}px ${+s.toFixed(3)}px`;
  const position = `${+mod(-cam.x * cam.z, s).toFixed(3)}px ${+mod(-cam.y * cam.z, s).toFixed(3)}px`;
  const c = bg.patternColor;
  const backgroundImage =
    bg.mode === 'dots'
      ? `radial-gradient(circle at 1px 1px, ${c} 1px, transparent 1.5px)`
      : bg.mode === 'grid'
        ? `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`
        : bg.mode === 'lines'
          ? `linear-gradient(${c} 1px, transparent 1px)`
          : 'none';
  return { backgroundColor: bg.color, backgroundImage, backgroundSize: size, backgroundPosition: position };
}

/** Ink and fill of objects without their own colours: dark on light boards, light on dark ones (not the UI theme). */
export function canvasColors(bg: BoardMeta['background']): { ink: string; fill: string } {
  const hex = /^#([0-9a-f]{6})$/i.exec(bg.color)?.[1] ?? 'ffffff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];
  const light = 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5;
  return light ? { ink: '#141414', fill: '#ffffff' } : { ink: '#f4f4f5', fill: '#27272a' };
}
